import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import tStore from 'types/store';
import { tGetAll } from 'types/global';
import { tGetAllDevicesData } from 'types/services/devices';
import {
  Spinner,
  PageWrapper,
  ComplexTable,
  Breadcrumbs,
  Tiles,
  ListTiles,
  ListTitle,
} from 'components';
import { useFetch } from 'hooks';
import devicesAndFacilities from 'services/devices';
import { ListBreadcrumb, ListTableContainer } from 'styles/GlobalStyledComponents';
import { tableHead } from './TableConfig';

const DevicesAndFacilitiesList: React.FC = (): JSX.Element => {
  const { t } = useTranslation();
  const history = useHistory();

  const permissions = useSelector((state: tStore) => state.user.permissions);
  const canWrite = !!permissions.includes('tunAppliancesWrite');

  const [limit, setLimit] = useState<number>(10);
  const [offset, setOffset] = useState<number>(0);
  const [search, setSearch] = useState<string>('');
  const [sort, setSort] = useState<string>('');

  const {
    fetch: setParamsHandler,
    loading,
    data,
    count,
  } = useFetch<tGetAllDevicesData, tGetAll>(devicesAndFacilities.getAll);

  const fetchData = useCallback(
    () => setParamsHandler({ search, limit, offset, ordering: sort }),
    [search, limit, offset, sort, setParamsHandler],
  );

  const setSortParams = (field: string, sort: string) => {
    const type: string = sort ? '-' : '';
    setSort(`${type}${field}`);
  };

  const setRequestParams = useCallback(
    (newOffset: number, newLimit: number) => {
      if (newOffset !== offset) setOffset(newOffset);
      if (newLimit !== limit) setLimit(newLimit);
    },
    [limit, offset],
  );

  useEffect(() => {
    setOffset(0);
  }, [search]);

  useEffect(() => {
    fetchData();
  }, [limit, offset, sort, fetchData]);

  const showNewDeviceForm = () => history.push('/devices/new');

  const showDeviceDetails = (id: number | string) => history.push(`/devices/${id}`);

  return (
    <PageWrapper>
      {loading && <Spinner />}
      <ListBreadcrumb>
        <Breadcrumbs />
      </ListBreadcrumb>
      <ListTitle title={t('Devices and Facilities')} />
      <ListTiles>
        <Tiles
          text={t('Add new device or facility')}
          icon='construction'
          bgIcon='addComment'
          color='orange'
          variant='filled'
          onClick={showNewDeviceForm}
          disabled={!canWrite}
        />
      </ListTiles>
      <ListTableContainer>
        <ComplexTable
          isRowClickable
          onRowClick={showDeviceDetails}
          value={search}
          resetValue={() => setSearch('')}
          onChange={({ target: { value } }) => setSearch(value)}
          name='tableSearch'
          tableBodyData={data}
          tableHead={tableHead}
          setSortParams={(field: string, sort: string) => setSortParams(field, sort)}
          totalRecords={count}
          setParams={(offset: number, limit: number) => setRequestParams(offset, limit)}
          tableName='List of devices and facilities'
          limit={limit}
          offset={offset}
        />
      </ListTableContainer>
    </PageWrapper>
  );
};

export default DevicesAndFacilitiesList;
